// =============================================================================
// API base URL resolution and the recipe calls.
//
// The SPA is served from app.<something> and the API lives at api.<something>,
// so the base URL can be derived from the current hostname. That means the same
// build artifact works on dev and prod without a per-stage rebuild — which
// matters, because the CDK app deploys whatever is in dist/ to both.
//
// VITE_API_BASE_URL overrides it for local development.
// =============================================================================

function resolveBaseUrl(): string {
    const override = import.meta.env.VITE_API_BASE_URL;
    if (override) return override.replace(/\/$/, '');

    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
        throw new Error(
            'Set VITE_API_BASE_URL in .env.local to point local dev at a deployed API.',
        );
    }

    return `https://${host.replace(/^app\./, 'api.')}`;
}

// ── The shared key ───────────────────────────────────────────────────────────
// One passphrase, known to both of you, held in localStorage and sent as a
// bearer token. This is a shared secret, not per-user auth: it gives no audit
// trail and rotating it logs both of you out. For a two-person recipe box that
// is the right trade; for anything with more people, it is not.

const KEY_STORAGE = 'recipes.key';

export function getKey(): string | null {
    return window.localStorage.getItem(KEY_STORAGE);
}

export function setKey(key: string): void {
    window.localStorage.setItem(KEY_STORAGE, key.trim());
}

export function clearKey(): void {
    window.localStorage.removeItem(KEY_STORAGE);
}

// ── Transport ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
    constructor(readonly status: number, message: string) {
        super(message);
        this.name = 'ApiError';
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const key = getKey();

    const response = await fetch(`${resolveBaseUrl()}/v1${path}`, {
        ...init,
        headers: {
            Accept: 'application/json',
            ...(key ? { Authorization: `Bearer ${key}` } : {}),
            ...init?.headers,
        },
    });

    if (!response.ok) {
        let message = `${init?.method ?? 'GET'} /v1${path} returned ${response.status}`;
        try {
            const body = await response.json();
            if (body?.error) message = body.error;
        } catch {
            // Non-JSON error body — the status alone is the whole signal.
        }
        throw new ApiError(response.status, message);
    }

    // 204 has no body to parse.
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
}

// ── Recipes ──────────────────────────────────────────────────────────────────

export interface Recipe {
    id: string;
    title: string;
    servings: string;
    totalTime: string;
    source: string;
    notes: string;
    ingredients: string[];
    method: string[];
    tags: string[];
    createdAt: string;
    updatedAt: string;
}

/** What the editor sends. Arrays arrive as newline-separated text; the handler splits them. */
export interface RecipeDraft {
    id?: string;
    title: string;
    servings: string;
    totalTime: string;
    source: string;
    notes: string;
    ingredients: string;
    method: string;
    tags: string;
}

export function listRecipes(): Promise<{ recipes: Recipe[] }> {
    return request<{ recipes: Recipe[] }>('/recipes');
}

export function getRecipe(id: string): Promise<Recipe> {
    return request<Recipe>(`/recipes/${encodeURIComponent(id)}`);
}

export function saveRecipe(draft: RecipeDraft): Promise<Recipe> {
    return request<Recipe>('/recipes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
    });
}

// POST rather than DELETE: the API's default CORS allows GET, POST and OPTIONS
// only, so keeping to two verbs avoids an apiCors override in bin/app.ts.
export function deleteRecipe(id: string): Promise<void> {
    return request<void>(`/recipes/${encodeURIComponent(id)}/delete`, { method: 'POST' });
}